import {analyticsLogger} from 'analytics/analyticsLogger';
import {showSearchAtom} from 'atoms/search';
import {useDocumentTitle} from 'hooks/useDocumentTitle';
import {t} from 'i18next';
import {useAtom, useAtomValue, useSetAtom} from 'jotai';
import {atomWithStorage} from 'jotai/utils';
import {useEffect, useRef, useState} from 'react';
import {useSearchParams} from 'react-router';

import {SearchBox} from './SearchBox';
import {meetingsPreviewAtom} from './useMeetings';
import {useAnchorRect, useUnroll} from './useOpenFromField';

/** The header's height (16 + 28 + 16, plus its hairline), for the opening animation. */
const HEADER_HEIGHT = 61;
/** Room left under the box when it opens on the home field. */
const BOTTOM_GAP = 48;

/**
 * Pane only when something is picked, or always there: still being decided.
 * `?search_pane=select` or `?search_pane=always` on any page switches it, and
 * it sticks in this browser.
 */
const paneOnSelectAtom = atomWithStorage('search:paneOnSelect', false);

/**
 * Today's box's instant answer card and filter chips, off by default:
 * `?search_extras=1` or `?search_extras=0` on any page switches them, and it
 * sticks in this browser.
 */
const extrasAtom = atomWithStorage('search:extras', false);

/** A `?s=` value, decoded the way today's box does (a stray % stays literal). */
export const queryFromParam = (value: string | null) =>
  value ? decodeURIComponent(value.replace(/%(?![A-Fa-f0-9]{2})/g, '%25')).trim() : '';

/**
 * The relaunched search box in the app's search overlay (behind
 * `ask_relaunch`; today's box is GlobalSearch). It starts from a `?s=` query,
 * logs SEARCH_SHOWN with where it was opened from, opens the only result for a
 * `?s=` link. On the home page it opens right on the home search field (its
 * left edge, top and width) and unrolls downward, so it feels like the field
 * itself opening; elsewhere it's centered in the overlay.
 */
export const GlobalSearchBox = () => {
  const [searchParams] = useSearchParams();
  const showSearch = useAtomValue(showSearchAtom);
  const [paneOnSelect, setPaneOnSelect] = useAtom(paneOnSelectAtom);
  const [extras, setExtras] = useAtom(extrasAtom);
  const setMeetingsPreview = useSetAtom(meetingsPreviewAtom);
  const [query, setQuery] = useState(() => queryFromParam(searchParams.get('s')));
  const ref = useRef<HTMLDivElement>(null);
  // On the home page, wherever search was opened from (its field, the top
  // bar, ⌘K), the box opens on the home field, so it always sits in one place.
  const field = useAnchorRect();
  useUnroll(ref, {enabled: field !== null, headerHeight: HEADER_HEIGHT});

  // The page behind holds still: wheel and arrow keys are the box's.
  useEffect(() => {
    const {overflow} = document.body.style;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = overflow;
    };
  }, []);

  useEffect(() => {
    analyticsLogger().logEvent('SEARCH_SHOWN', {source: showSearch});
    const pane = searchParams.get('search_pane');
    if (pane === 'select' || pane === 'always') setPaneOnSelect(pane === 'select');
    const extrasParam = searchParams.get('search_extras');
    if (extrasParam === '1' || extrasParam === '0') setExtras(extrasParam === '1');
    const meetingsParam = searchParams.get('search_meetings');
    setMeetingsPreview(
      meetingsParam === 'none' || meetingsParam === 'unlinked' ? meetingsParam : null
    );
    // Once per open, like today's box; later changes to the source don't count.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useDocumentTitle(t('searching') + (query.length > 0 ? ` "${query}"` : '...'), true);

  return (
    <div
      ref={ref}
      style={
        field
          ? {position: 'fixed', left: field.left, top: field.top, width: field.width}
          : undefined
      }
    >
      <SearchBox
        query={query}
        setQuery={setQuery}
        paneOnSelect={paneOnSelect}
        extras={extras}
        logSelections
        openSingleResult={showSearch === 'queryparam'}
        {...(field && {
          width: field.width,
          bodyHeight: `min(800px, calc(100vh - ${field.top + HEADER_HEIGHT + BOTTOM_GAP}px))`,
        })}
      />
    </div>
  );
};
